import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import {
  Bell,
  Search,
  Filter,
  RefreshCw,
  Eye,
  Trash2,
  MoreHorizontal,
  CheckCircle2,
  Clock,
  AlertCircle,
  CreditCard,
  DollarSign,
  User,
  Database,
  Shield,
  Webhook,
  Key,
  Mail,
  Smartphone,
  Download,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useNotificationStore } from '@/stores/notificationStore'
import { Notification } from '@/types'
import { formatDistanceToNow } from 'date-fns'

const notificationTypeConfig = {
  transaction: {
    label: 'Transaction',
    icon: CreditCard,
    color: 'text-blue-600 bg-blue-50 border-blue-200',
  },
  payment: {
    label: 'Payment',
    icon: DollarSign,
    color: 'text-green-600 bg-green-50 border-green-200',
  },
  account: {
    label: 'Account',
    icon: User,
    color: 'text-purple-600 bg-purple-50 border-purple-200',
  },
  system: { label: 'System', icon: Database, color: 'text-gray-600 bg-gray-50 border-gray-200' },
  security: { label: 'Security', icon: Shield, color: 'text-red-600 bg-red-50 border-red-200' },
  webhook: {
    label: 'Webhook',
    icon: Webhook,
    color: 'text-orange-600 bg-orange-50 border-orange-200',
  },
  api: { label: 'API', icon: Key, color: 'text-indigo-600 bg-indigo-50 border-indigo-200' },
  marketing: { label: 'Marketing', icon: Mail, color: 'text-pink-600 bg-pink-50 border-pink-200' },
}

const priorityConfig = {
  urgent: { label: 'Urgent', color: 'text-red-600 bg-red-50 border-red-200' },
  high: { label: 'High', color: 'text-orange-600 bg-orange-50 border-orange-200' },
  normal: { label: 'Normal', color: 'text-blue-600 bg-blue-50 border-blue-200' },
  low: { label: 'Low', color: 'text-gray-600 bg-gray-50 border-gray-200' },
}

const deliveryMethodConfig = {
  in_app: { label: 'In-App', icon: Bell },
  email: { label: 'Email', icon: Mail },
  sms: { label: 'SMS', icon: Smartphone },
  push: { label: 'Push', icon: Smartphone },
  webhook: { label: 'Webhook', icon: Webhook },
}

export default function Notifications() {
  const { branding } = useBranding()
  const pageStyles = getPageStyles(branding)
  const cardStyles = getCardStyles(branding)
  const statusColors = getStatusColors(branding)
  const [searchParams, setSearchParams] = useSearchParams()
  const { isAuthenticated } = useAuthStore()
  const {
    notifications,
    unreadCount,
    isLoading,
    fetchNotifications,
    markAsRead,
    deleteNotification,
  } = useNotificationStore()

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    type: searchParams.get('type') || '',
    priority: searchParams.get('priority') || '',
    status: searchParams.get('status') || '',
  })
  const [selectedNotifications, setSelectedNotifications] = useState<number[]>([])
  const [showFilters, setShowFilters] = useState(false)

  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications({ page: 1, limit: 50 })
    }
  }, [isAuthenticated, fetchNotifications])

  const handleFilterChange = (key: string, value: string) => {
    const newFilters = { ...filters, [key]: value }
    setFilters(newFilters)

    // Update URL params
    const params = new URLSearchParams()
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v) params.set(k, v)
    })
    setSearchParams(params)
  }

  const handleSearch = (value: string) => {
    handleFilterChange('search', value)
  }

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id)
    }
    if (notification.action_url) {
      window.open(notification.action_url, '_blank')
    }
  }

  const handleSelectNotification = (id: number) => {
    setSelectedNotifications(prev =>
      prev.includes(id) ? prev.filter(n => n !== id) : [...prev, id]
    )
  }

  const handleSelectAll = () => {
    if (selectedNotifications.length === filteredNotifications.length) {
      setSelectedNotifications([])
    } else {
      setSelectedNotifications(filteredNotifications.map(n => n.id))
    }
  }

  const handleBulkMarkAsRead = async () => {
    for (const id of selectedNotifications) {
      await markAsRead(id)
    }
    setSelectedNotifications([])
  }

  const handleBulkDelete = async () => {
    for (const id of selectedNotifications) {
      await deleteNotification(id)
    }
    setSelectedNotifications([])
  }

  const filteredNotifications = notifications.filter(notification => {
    const matchesSearch =
      !filters.search ||
      notification.title.toLowerCase().includes(filters.search.toLowerCase()) ||
      notification.message.toLowerCase().includes(filters.search.toLowerCase())

    const matchesType = !filters.type || notification.notification_type === filters.type
    const matchesPriority = !filters.priority || notification.priority === filters.priority
    const matchesStatus =
      !filters.status ||
      (filters.status === 'read' && notification.is_read) ||
      (filters.status === 'unread' && !notification.is_read)

    return matchesSearch && matchesType && matchesPriority && matchesStatus
  })

  const getNotificationConfig = (type: string) => {
    return (
      notificationTypeConfig[type as keyof typeof notificationTypeConfig] ||
      notificationTypeConfig.system
    )
  }

  const getPriorityConfig = (priority: string) => {
    return priorityConfig[priority as keyof typeof priorityConfig] || priorityConfig.normal
  }

  const getDeliveryMethodConfig = (method: string) => {
    return (
      deliveryMethodConfig[method as keyof typeof deliveryMethodConfig] ||
      deliveryMethodConfig.in_app
    )
  }

  return (
    <div
      className="min-h-screen"
      style={pageStyles.container}
    >
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-3xl font-bold"
                style={pageStyles.text}
              >
                Notifications
              </h1>
              <p
                className="mt-2"
                style={pageStyles.textSecondary}
              >
                Manage your notifications and alerts
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => fetchNotifications({ page: 1, limit: 50, refresh: true })}
                disabled={isLoading}
                className="btn-outline"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button className="btn-gradient">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="card" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}>
            <div className="card-content">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium" style={pageStyles.textSecondary}>Total Notifications</p>
                  <p className="text-2xl font-bold" style={pageStyles.text}>{notifications.length}</p>
                </div>
                <div className="p-3 rounded-full" style={{ backgroundColor: statusColors.info.backgroundColor }}>
                  <Bell className="h-6 w-6" style={{ color: statusColors.info.color }} />
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ ...cardStyles, borderColor: statusColors.error.borderColor }}>
            <div className="card-content">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium" style={pageStyles.textSecondary}>Unread</p>
                  <p className="text-2xl font-bold" style={pageStyles.text}>{unreadCount}</p>
                </div>
                <div className="p-3 rounded-full" style={{ backgroundColor: statusColors.error.backgroundColor }}>
                  <AlertCircle className="h-6 w-6" style={{ color: statusColors.error.color }} />
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ ...cardStyles, borderColor: statusColors.warning.borderColor }}>
            <div className="card-content">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium" style={pageStyles.textSecondary}>High Priority</p>
                  <p className="text-2xl font-bold" style={pageStyles.text}>
                    {
                      notifications.filter(n => n.priority === 'high' || n.priority === 'urgent')
                        .length
                    }
                  </p>
                </div>
                <div className="p-3 rounded-full" style={{ backgroundColor: statusColors.warning.backgroundColor }}>
                  <AlertCircle className="h-6 w-6" style={{ color: statusColors.warning.color }} />
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-content">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium" style={pageStyles.textSecondary}>Today</p>
                  <p className="text-2xl font-bold" style={pageStyles.text}>
                    {
                      notifications.filter(n => {
                        const today = new Date()
                        const notificationDate = new Date(n.created_at)
                        return notificationDate.toDateString() === today.toDateString()
                      }).length
                    }
                  </p>
                </div>
                <div className="p-3 rounded-full" style={{ backgroundColor: statusColors.success.backgroundColor }}>
                  <Clock className="h-6 w-6" style={{ color: statusColors.success.color }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="card mb-6" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}>
          <div className="card-content">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4" style={{ color: branding.textColor }} />
                  <input
                    type="text"
                    placeholder="Search notifications..."
                    value={filters.search}
                    onChange={e => handleSearch(e.target.value)}
                    className="input pl-10" style={{ borderColor: branding.textColor }}
                  />
                </div>
              </div>

              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="btn-outline"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </Button>

              {selectedNotifications.length > 0 && (
                <div className="flex items-center space-x-2">
                  <Button variant="outline" onClick={handleBulkMarkAsRead} className="btn-outline" style={{ backgroundColor: statusColors.success.backgroundColor }}>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Mark as Read
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleBulkDelete}
                    className="btn-outline text-red-600 hover:text-red-700" style={{ backgroundColor: statusColors.error.backgroundColor }}>
                    <Trash2 className="h-4 w-4 mr-2" style={{ color: statusColors.error.color }} />
                    Delete
                  </Button>
                </div>
              )}
            </div>
            {/* Advanced Filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <select
                    value={filters.type}
                    onChange={e => handleFilterChange('type', e.target.value)}
                    className="input"
                  >
                    <option value="">All Types</option>
                    {Object.entries(notificationTypeConfig).map(([key, config]) => (
                      <option key={key} value={key}>
                        {config.label}
                      </option>
                    ))}
                  </select>

                  <select
                    value={filters.priority}
                    onChange={e => handleFilterChange('priority', e.target.value)}
                    className="input"
                  >
                    <option value="">All Priorities</option>
                    {Object.entries(priorityConfig).map(([key, config]) => (
                      <option key={key} value={key}>
                        {config.label}
                      </option>
                    ))}
                  </select>

                  <select
                    value={filters.status}
                    onChange={e => handleFilterChange('status', e.target.value)}
                    className="input"
                  >
                    <option value="">All Status</option>
                    <option value="read">Read</option>
                    <option value="unread">Unread</option>
                  </select>

                  <Button
                    variant="outline"
                    onClick={() => {
                      setFilters({ search: '', type: '', priority: '', status: '' })
                      setSearchParams({})
                    }}
                    className="btn-outline"
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Notifications List */}
        <div className="card" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}>
          <div className="card-content p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin mr-3" style={{ color: branding.textColor }} />
                <p className="text-gray-500" style={pageStyles.textSecondary}>Loading notifications...</p>
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Bell className="h-12 w-12 mb-4" style={{ color: branding.textColor }} />
                <p className="text-gray-500 mb-4" style={pageStyles.textSecondary}>No notifications found</p>
                <Button
                  onClick={() => {
                    setFilters({ search: '', type: '', priority: '', status: '' })
                    setSearchParams({})
                  }}
                  className="btn-outline" style={{ backgroundColor: statusColors.success.backgroundColor }}
                >
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-gray-200" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}>
                {/* Select All Header */}
                <div className="px-6 py-3 bg-gray-50 border-b border-gray-200" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}>
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={
                        selectedNotifications.length === filteredNotifications.length &&
                        filteredNotifications.length > 0
                      }
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500" style={{ color: statusColors.error.color }}
                    />
                    <span className="text-sm text-gray-600" style={pageStyles.textSecondary}>
                      {selectedNotifications.length > 0
                        ? `${selectedNotifications.length} selected`
                        : 'Select all notifications'}
                    </span>
                  </div>
                </div>

                {/* Notifications */}
                {filteredNotifications.map(notification => {
                  const typeConfig = getNotificationConfig(notification.notification_type)
                  const priorityConfig = getPriorityConfig(notification.priority)
                  const deliveryConfig = getDeliveryMethodConfig(notification.delivery_method)
                  const TypeIcon = typeConfig.icon
                  const DeliveryIcon = deliveryConfig.icon

                  return (
                    <div
                      key={notification.id}
                      className={`p-6 hover:bg-gray-50 transition-colors ${!notification.is_read ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                        }`}
                    >
                      <div className="flex items-start space-x-4">
                        <input
                          type="checkbox"
                          checked={selectedNotifications.includes(notification.id)}
                          onChange={() => handleSelectNotification(notification.id)}
                          className="h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500 mt-1" style={{ color: statusColors.error.color }}
                        />

                        <div className={`p-2 rounded-lg`} style={{ backgroundColor: statusColors.info.backgroundColor }}>
                          <TypeIcon className="h-5 w-5" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3
                                className={`text-lg font-medium ${!notification.is_read ? 'text-gray-900' : 'text-gray-700'}`} style={pageStyles.text}
                              >
                                {notification.title}
                              </h3>
                              <p className="text-sm text-gray-600 mt-1 line-clamp-2" style={pageStyles.textSecondary}>
                                {notification.message}
                              </p>

                              <div className="flex items-center space-x-4 mt-3">
                                <span
                                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${typeConfig.color}`}
                                  style={{ backgroundColor: statusColors.info.backgroundColor }}
                                >
                                  <TypeIcon className="h-3 w-3 mr-1" />
                                  {typeConfig.label}
                                </span>

                                <span
                                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${priorityConfig.color}`}
                                  style={{ backgroundColor: statusColors.warning.backgroundColor }}
                                >
                                  {priorityConfig.label}
                                </span>

                                <div className="flex items-center text-xs text-gray-500" style={{ color: statusColors.info.color }}>
                                  <DeliveryIcon className="h-3 w-3 mr-1" />
                                  {deliveryConfig.label}
                                </div>

                                <span className="text-xs text-gray-400" style={pageStyles.textSecondary}>
                                  {formatDistanceToNow(new Date(notification.created_at), {
                                    addSuffix: true,
                                  })}
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center space-x-2 ml-4">
                              {!notification.is_read && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800" style={{ backgroundColor: statusColors.info.backgroundColor }}>
                                  New
                                </span>
                              )}

                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleNotificationClick(notification)}
                                className="h-8 w-8 p-0" style={{ color: statusColors.info.color }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>

                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteNotification(notification.id)}
                                className="h-8 w-8 p-0 text-red-600 hover:text-red-700" style={{ color: statusColors.error.color }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>

                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" style={{ color: statusColors.info.color }}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
